<template>
  <FlexCol gap-3>
    <Flex gap-3 justify-end>
      <InputZoom database="itemWarehouse" item-value="id" item-text="name" placeholder="Pesquisar" :item-autocomplete="['code', 'name']" class="w-[400px]" clearable v-model="selectedItem">
        <template #selected="{ item }"> {{ item.code }} {{ item.name }} </template>
      </InputZoom>

      <Button label="Exportar" primary icon="fa-solid fa-arrow-up-from-bracket" @click="exportStock()" />
    </Flex>
    <TheLoader v-if="loader" />
    <TableFull :headers="stockHeaders" :items="itemWarehouse" v-model="control" @doubleClick="handleDoubleClick" dense>
      <template v-slot:itemCode="{ item }">{{ item.itemCode }} </template>
      <template v-slot:total="{ item }">{{ formatMoneyS(item.total) }} </template>
      <template v-slot:actions="{ item }">
        <Flex gap-3>
          <TheToolTip label="Exibir histórico">
            <TableIconButton icon="fa-eye" @click="viewHistory(item)" />
          </TheToolTip>
        </Flex>
      </template>
    </TableFull>
  </FlexCol>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue'
import { get, getFile } from '@/crud'
import { useRouter } from 'vue-router'
import { useFormat } from '@/composables/useFormat'

const router = useRouter()
const { formatMoneyS } = useFormat()

// Props
const props = defineProps({
  id: [String, Number],
  warehouseName: String,
  type: String,
  card: Boolean,
})
//  Varibles
const api = 'itemWarehouse'

const control = ref({
  total: 0,
  limit: 50,
  pages: 0,
  page: 1,
  order: [{ direction: 'ASC', column: 'code', table: 'item', name: 'Código', raw: 'cast(item.code as SIGNED)' }],
})

const loader = ref(false)

const itemWarehouse = ref([])
const selectedItem = ref(null)

const stockHeaders = computed(() => {
  // Se for depósito tipo 'physic' (físico), mostra apenas colunas básicas
  if (props.type === 'physic') {
    return [
      { title: 'Código', field: 'itemCode', table: 'item', orderField: 'code', raw: 'cast(item.code as SIGNED)' },
      { title: 'Material', field: 'itemName', table: 'item', orderField: 'name', itemClass: '!text-wrap' },
      {
        title: 'Und Medida',
        field: 'criterionOfMeasurementSymbol',
        table: 'criterionOfMeasurement',
        orderField: 'symbol',
        width: '120px',
      },
      { title: 'Saldo', field: 'quantity', width: '120px' },
      { title: 'Preço Médio', field: 'avgPrice', width: '140px', format: formatMoneyS },
      { title: 'Último Preço', field: 'lastPrice', width: '160px', format: formatMoneyS },
    ]
  }

  if (props.type === 'team' && props.card) {
    return [
      { title: 'Código', field: 'itemCode', table: 'item', orderField: 'code', raw: 'cast(item.code as SIGNED)' },
      { title: 'Material', field: 'itemName', table: 'item', orderField: 'name', itemClass: '!text-wrap' },
      { title: 'Saldo', field: 'quantity', width: '120px' },
      { title: 'Ações', field: 'actions', width: '120px', headerClass: 'justify-center', disableOrder: true },
    ]
  }

  // Para outros tipos de depósito (team, vehicles), mostra todas as colunas
  return [
    { title: 'Código', field: 'itemCode', table: 'item', orderField: 'code', raw: 'cast(item.code as SIGNED)' },
    { title: 'Material', field: 'itemName', table: 'item', orderField: 'name', itemClass: '!text-wrap' },
    {
      title: 'Und Medida',
      field: 'criterionOfMeasurementSymbol',
      table: 'criterionOfMeasurement',
      orderField: 'symbol',
      width: '120px',
    },
    { title: 'Fornecido', field: 'supplied', width: '120px' },
    { title: 'Aplicado', field: 'applied', width: '120px' },
    { title: 'Saldo', field: 'quantity', width: '120px' },
    { title: 'Preço Médio', field: 'avgPrice', width: '140px', format: formatMoneyS },
    { title: 'Último Preço', field: 'lastPrice', width: '160px', format: formatMoneyS },
    { title: 'Total', field: 'total', width: '120px' },
    { title: 'Ações', field: 'actions', width: '120px', headerClass: 'justify-center', disableOrder: true },
  ]
})

// Methods
const loadItems = async (page = 1) => {
  const filters = [
    {
      column: 'idWarehouse',
      value: props.id,
      operator: '=',
    },
  ]

  if (selectedItem.value) {
    filters.push({
      column: 'idItem',
      value: selectedItem.value,
      operator: '=',
    })
  }

  const query = {
    params: {
      page,
      limit: control.value.limit,
      order: control.value.order,
      filters,
    },
  }

  loader.value = true
  const raw = await get(api, query)

  loader.value = false
  itemWarehouse.value = raw.data
  control.value.pages = Math.ceil(raw.total / control.value.limit)
  control.value.total = raw.total
}

const exportStock = async () => {
  const localFilters = [
    {
      column: 'idWarehouse',
      value: props.id,
      operator: '=',
    },
  ]

  if (selectedItem.value) {
    localFilters.push({
      column: 'idItem',
      value: selectedItem.value,
      operator: '=',
    })
  }

  // Monta o nome do arquivo com nome do depósito e data
  const today = new Date()
  const dateStr = today.toLocaleDateString('pt-BR').replace(/\//g, '-')
  const fileName = `items-do-deposito-${props.warehouseName || 'sem-nome'}-${dateStr}`

  const query = {
    params: {
      filters: localFilters,
      order: control.value.order,
      limit: 'Infinity',
    },
  }

  const raw = await get(api, query)

  const exportHeaders = stockHeaders.value
    .filter((header) => header.field !== 'actions')
    .map((header) => ({
      title: header.title,
      field: header.field,
    }))

  const exportItems = (raw.data || []).map((item) => ({
    ...item,
    avgPrice: formatMoneyS(item.avgPrice ?? 0),
    lastPrice: formatMoneyS(item.lastPrice ?? 0),
    total: formatMoneyS(item.total ?? 0),
  }))

  await getFile({}, fileName, exportHeaders, null, 'exporter/xlsx', exportItems)
}

const viewHistory = (item) => {
  router.push({
    name: 'itemApplicationHistory',
    params: { idItem: item.idItem, idWarehouse: props.id },
  })
}
//  Watchers
watch(
  () => control.value,
  () => {
    loadItems(control.value.page || 1)
  },
  { deep: true },
)

watch(
  () => props.id,
  (newVal) => {
    if (newVal) {
      loadItems()
    }
  },
)

watch(selectedItem, () => {
  loadItems(1)
})
// Life Cycle
onMounted(() => {
  loadItems()
})
</script>
